/**
 * ImageCapture
 *
 * Handles high-resolution viewport capture and mask corner storage.
 * Converts WebGL renderbuffer to canvas and manages pixel budget calculations.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { CapturedImage } from '../../../../utils/segmentation/ISegmentationStrategy';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';

export class ImageCapture {
  private viewer: Viewer;
  private coordinateTransformer: CoordinateTransformer;

  constructor(viewer: Viewer, coordinateTransformer: CoordinateTransformer) {
    this.viewer = viewer;
    this.coordinateTransformer = coordinateTransformer;
  }

  /**
   * Capture and encode an area of the viewport
   * Returns the captured image data
   */
  async captureArea(start: THREE.Vector2, end: THREE.Vector2, skipCrop = false): Promise<CapturedImage | null> {
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);

    if (width < 10 || height < 10) {
      return null; // Too small
    }

    // Store mask corners for coordinate transformations
    const minX = Math.min(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const origin = new THREE.Vector2(minX, minY);
    this.storeMaskCorners(origin, width, height);

    // Calculate WebGL origin (bottom-left coordinate system)
    const webglOrigin = new THREE.Vector2(Math.min(start.x, end.x), Math.min(this.viewer.renderArea.clientHeight - start.y, this.viewer.renderArea.clientHeight - end.y));

    // Calculate resolution multiplier based on pixel budget (target 2.25 megapixels)
    const currentPixels = width * height;
    const targetPixelBudget = 2250000; // 2.25 megapixels
    const resolutionMultiplier = currentPixels > 0 ? Math.sqrt(targetPixelBudget / currentPixels) : 1;

    // Use screenshotRenderer to render a high-quality screenshot
    const screenshotData = this.viewer.screenshotRenderer.renderScreenshot(
      webglOrigin,
      width,
      height,
      false, // renderObjects - don't render measurement objects etc.
      resolutionMultiplier,
      skipCrop,
    );

    // Verify dimensions and buffer size
    const actualPixelWidth = Math.round(screenshotData.pixelWidth);
    const actualPixelHeight = Math.round(screenshotData.pixelHeight);
    const expectedBufferSize = actualPixelWidth * actualPixelHeight * 4;

    if (screenshotData.buffer.length !== expectedBufferSize) {
      console.error(`Buffer size mismatch: ${actualPixelWidth}x${actualPixelHeight} expects ${expectedBufferSize} bytes, got ${screenshotData.buffer.length}`);
      return null;
    }

    // Convert buffer to canvas
    const canvas = document.createElement('canvas');
    canvas.width = actualPixelWidth;
    canvas.height = actualPixelHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const imageData = ctx.createImageData(actualPixelWidth, actualPixelHeight);

    // Safety check before setting buffer
    if (imageData.data.length !== screenshotData.buffer.length) {
      console.error(`ImageData size mismatch: canvas expects ${imageData.data.length} bytes, buffer has ${screenshotData.buffer.length} bytes`);
      return null;
    }

    imageData.data.set(screenshotData.buffer);

    // Force all alpha values to 255 (fully opaque)
    for (let i = 3; i < imageData.data.length; i += 4) {
      imageData.data[i] = 255;
    }

    ctx.putImageData(imageData, 0, 0);

    // Return the captured image
    return {
      canvas: canvas,
      width: actualPixelWidth,
      height: actualPixelHeight,
    };
  }

  /**
   * Capture a position map for the same viewport region as the last captureArea call.
   * Each pixel encodes the EYE-SPACE XYZ of the point that landed there
   * (12+12+8 bits across RGBA). The reader converts back to world position
   * via `inverseViewMatrix * eyeXYZ`. Returns null if dimensions or area
   * are invalid.
   */
  capturePositionMap(start: THREE.Vector2, end: THREE.Vector2, targetWidth: number, targetHeight: number, zRange?: [number, number], worldBoxCorners?: THREE.Vector3[]): {
    imageData: ImageData;
    eyeMin: [number, number, number];
    eyeMax: [number, number, number];
    inverseViewMatrix: THREE.Matrix4;
  } | null {
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);

    if (width < 10 || height < 10) return null;

    // Calculate WebGL origin (bottom-left coordinate system) - same as captureArea
    const webglOrigin = new THREE.Vector2(Math.min(start.x, end.x), Math.min(this.viewer.renderArea.clientHeight - start.y, this.viewer.renderArea.clientHeight - end.y));

    // Calculate resolution multiplier to match the captured image dimensions
    const currentPixels = width * height;
    const targetPixelBudget = 2250000;
    const resolutionMultiplier = currentPixels > 0 ? Math.sqrt(targetPixelBudget / currentPixels) : 1;

    const result = this.viewer.screenshotRenderer.renderPositionMap(webglOrigin, width, height, resolutionMultiplier, zRange, { width: targetWidth, height: targetHeight }, worldBoxCorners);

    if (!result) return null;

    // Convert buffer to ImageData - do NOT force alpha to 255 (alpha carries XY low bits)
    const imageData = new ImageData(new Uint8ClampedArray(result.buffer), result.pixelWidth, result.pixelHeight);

    return { imageData, eyeMin: result.eyeMin, eyeMax: result.eyeMax, inverseViewMatrix: result.inverseViewMatrix };
  }

  /**
   * Store mask corners in world space for camera-independent tracking
   */
  private storeMaskCorners(origin: THREE.Vector2, width: number, height: number): void {
    // Unproject the 4 corners of the selected area to world space
    const screenCorners = [new THREE.Vector2(origin.x, origin.y), new THREE.Vector2(origin.x + width, origin.y), new THREE.Vector2(origin.x + width, origin.y + height), new THREE.Vector2(origin.x, origin.y + height)];

    const maskCorners = screenCorners.map((corner) => {
      const worldPos = this.coordinateTransformer.screenToWorld(corner);
      return worldPos || new THREE.Vector3();
    });

    this.coordinateTransformer.setMaskCorners(maskCorners);
  }
}
